/**
 * Word-level diff for incremental track changes application.
 *
 * Uses LCS (Longest Common Subsequence) to produce an optimal word-level diff,
 * then groups consecutive changes into ChangeChunks with surrounding context
 * so the caller can search-and-apply each chunk individually in Word.
 */

/* ---------- public types ---------- */

export interface DiffOp {
  type: 'equal' | 'delete' | 'insert';
  words: string[];
}

/**
 * A single contiguous region of change within the paragraph.
 *
 * - `delete`:  some old words removed
 * - `insert`:  some new words added
 *
 * Note: there is no `replace` type. What was previously a replace is emitted
 * as a separate `delete` chunk followed by an `insert` chunk with the same
 * prefix/suffix context, so that all deletions can be applied before all
 * insertions.
 */
export interface ChangeChunk {
  type: 'delete' | 'insert';
  oldWords: string[];      // words to find/delete in the document  (empty for insert)
  newWords: string[];      // words to put in their place            (empty for delete)
  prefixWords: string[];   // up to 3 equal words BEFORE this change (for disambiguation)
  suffixWords: string[];   // up to 3 equal words AFTER  this change (for disambiguation)
}

/* ---------- helpers ---------- */

/** Split text into words on whitespace. */
function splitWords(text: string): string[] {
  return text.trim().split(/\s+/).filter(w => w.length > 0);
}

/* ---------- LCS-based word diff ---------- */

/**
 * Compute a word-level diff between `original` and `modified` using LCS.
 * Returns a list of merged DiffOps in forward (left-to-right) order.
 */
export function computeWordDiff(original: string, modified: string): DiffOp[] {
  const origWords = splitWords(original);
  const modWords  = splitWords(modified);
  const n = origWords.length;
  const m = modWords.length;

  // --- build LCS table ---
  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) {
    dp[i] = new Array(m + 1).fill(0);
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (origWords[i - 1] === modWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // --- backtrack to build raw ops (reverse order) ---
  const stack: DiffOp[] = [];
  let i = n;
  let j = m;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origWords[i - 1] === modWords[j - 1]) {
      stack.push({ type: 'equal', words: [origWords[i - 1]] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'insert', words: [modWords[j - 1]] });
      j--;
    } else {
      stack.push({ type: 'delete', words: [origWords[i - 1]] });
      i--;
    }
  }

  // --- reverse to forward order, then merge consecutive same-type ops ---
  stack.reverse();

  const merged: DiffOp[] = [];
  for (const op of stack) {
    if (merged.length > 0 && merged[merged.length - 1].type === op.type) {
      merged[merged.length - 1].words.push(...op.words);
    } else {
      merged.push({ type: op.type, words: [...op.words] });
    }
  }

  return merged;
}

/* ---------- grouping into ChangeChunks ---------- */

/**
 * Group diff operations into ChangeChunks, attaching up to 3 words of
 * surrounding equal context for disambiguation during Word search.
 */
export function groupIntoChangeChunks(diffs: DiffOp[]): ChangeChunk[] {
  const chunks: ChangeChunk[] = [];
  let idx = 0;

  while (idx < diffs.length) {
    // skip equal blocks
    if (diffs[idx].type === 'equal') {
      idx++;
      continue;
    }

    // collect consecutive non-equal ops into one chunk
    const oldWords: string[] = [];
    const newWords: string[] = [];
    const start = idx;

    while (idx < diffs.length && diffs[idx].type !== 'equal') {
      if (diffs[idx].type === 'delete') {
        oldWords.push(...diffs[idx].words);
      } else {
        newWords.push(...diffs[idx].words);
      }
      idx++;
    }

    // prefix context (up to 3 words from the preceding equal block)
    const prefixWords: string[] = [];
    if (start > 0 && diffs[start - 1].type === 'equal') {
      prefixWords.push(...diffs[start - 1].words.slice(-3));
    }

    // suffix context (up to 3 words from the following equal block)
    const suffixWords: string[] = [];
    if (idx < diffs.length && diffs[idx].type === 'equal') {
      suffixWords.push(...diffs[idx].words.slice(0, 3));
    }

    // Emit delete first, then insert (never a combined replace).
    // This allows the caller to apply all deletes before all inserts.
    if (oldWords.length > 0) {
      chunks.push({ type: 'delete', oldWords, newWords: [], prefixWords, suffixWords });
    }
    if (newWords.length > 0) {
      chunks.push({ type: 'insert', oldWords: [], newWords, prefixWords, suffixWords });
    }
  }

  return chunks;
}
